<template>
  <div ref="rootRef" class="tw-pb-1">
    <!-- Amounts are: Net (tax-exclusive) / Gross (tax-inclusive) -->
    <div class="tw-flex tw-items-center tw-gap-2 tw-mb-3">
      <span class="tw-text-sm tw-text-neutral-500">{{ t('orders.titles.amounts_are') }}</span>
      <div class="tw-inline-flex tw-rounded-md tw-border tw-border-neutral-200 tw-p-0.5">
        <button type="button" :class="segClass('net')" @click="setMode('net')">
          {{ t('orders.titles.amounts_are_net') }}
        </button>
        <button type="button" :class="segClass('gross')" @click="setMode('gross')">
          {{ t('orders.titles.amounts_are_gross') }}
        </button>
      </div>
    </div>

    <!-- Net | VAT | Gross on one line; stacks when the container itself is too narrow -->
    <div class="tw-grid tw-gap-3" :class="isNarrow ? 'tw-grid-cols-1' : 'tw-grid-cols-3'">
      <!-- Net -->
      <div>
        <Label>
          {{ t('orders.titles.amount_net') }}
          <span v-if="required && mode === 'net'" class="tw-text-red-600">*</span>
        </Label>
        <CurrencyInput
          :model-value="netLocal ?? undefined"
          :placeholder="t('orders.titles.enter_net_amount')"
          :readonly="mode === 'gross'"
          :class="mode === 'gross' ? LOCKED_CLASS : ''"
          @update:model-value="onNet"
        />
        <p v-if="netError" class="tw-text-danger tw-text-xs tw-mt-1">{{ netError }}</p>
      </div>

      <!-- VAT: rate (+ custom %) with the VAT amount shown as helper text -->
      <div>
        <Label>{{ t('orders.titles.vat') }}</Label>
        <Select :model-value="rateSelection" @update:model-value="onRateSelect">
          <SelectTrigger>
            <SelectValue :placeholder="t('orders.titles.vat')" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="19">19%</SelectItem>
            <SelectItem value="7">7%</SelectItem>
            <SelectItem value="0">0%</SelectItem>
            <SelectItem value="custom">{{ t('orders.titles.vat_custom') }}</SelectItem>
          </SelectContent>
        </Select>
        <div v-if="isCustom" class="tw-relative tw-mt-2">
          <Input
            type="number"
            inputmode="decimal"
            :model-value="customRate ?? ''"
            placeholder="0"
            class="tw-pr-6"
            @update:model-value="onCustomRate"
          />
          <span
            class="tw-absolute tw-right-2 tw-top-1/2 -tw-translate-y-1/2 tw-text-sm tw-text-neutral-400"
          >
            %
          </span>
        </div>
        <p v-if="vatDisplay" class="tw-text-neutral-500 tw-text-xs tw-mt-1">{{ vatDisplay }}</p>
      </div>

      <!-- Gross -->
      <div>
        <Label>
          {{ t('orders.titles.amount_gross') }}
          <span v-if="required && mode === 'gross'" class="tw-text-red-600">*</span>
        </Label>
        <CurrencyInput
          :model-value="grossLocal ?? undefined"
          :placeholder="t('orders.titles.enter_gross_amount')"
          :readonly="mode === 'net'"
          :class="mode === 'net' ? LOCKED_CLASS : ''"
          @update:model-value="onGross"
        />
        <p v-if="grossError" class="tw-text-danger tw-text-xs tw-mt-1">{{ grossError }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import CurrencyInput from '@/components/shared/currency-input/CurrencyInput.vue';

type Mode = 'net' | 'gross';
type Amount = number | string | null;

const props = withDefaults(
  defineProps<{
    net: Amount;
    gross: Amount;
    /** VAT rate as a percentage, e.g. "19". */
    vatRate?: string | null;
    netError?: string;
    grossError?: string;
    required?: boolean;
    /** Which amount you type: 'net' (tax-exclusive) or 'gross' (tax-inclusive). */
    defaultMode?: Mode;
  }>(),
  { vatRate: null, netError: '', grossError: '', required: true, defaultMode: 'net' }
);

const emit = defineEmits<{
  'update:net': [value: number | null];
  'update:gross': [value: number | null];
  'update:vatRate': [value: string | null];
}>();

const { t, locale } = useI18n();

// Stack the three fields based on the component's OWN width (container query), not the viewport —
// these forms live in a narrow side pane where a viewport breakpoint would keep them cramped.
const NARROW_WIDTH = 380;
const rootRef = ref<HTMLElement | null>(null);
const isNarrow = ref(false);
let resizeObserver: ResizeObserver | null = null;
onMounted(() => {
  if (!rootRef.value || typeof ResizeObserver === 'undefined') return;
  resizeObserver = new ResizeObserver(entries => {
    isNarrow.value = entries[0].contentRect.width < NARROW_WIDTH;
  });
  resizeObserver.observe(rootRef.value);
});
onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
});

// Greyed, non-editable look for the auto-calculated field (matches the read-only comparison field).
const LOCKED_CLASS =
  '!tw-bg-neutral-100 !tw-text-neutral-500 !tw-cursor-default !tw-border-neutral-200/50 !tw-shadow-none';

const PRESETS = ['19', '7', '0'];

const num = (v: Amount): number | null => {
  if (v === '' || v === null || v === undefined) return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return isNaN(n) ? null : n;
};
const round2 = (n: number) => Math.round(n * 100) / 100;

const mode = ref<Mode>(props.defaultMode);
const netLocal = ref<number | null>(num(props.net));
const grossLocal = ref<number | null>(num(props.gross));

// Rate is split into the dropdown selection and (when custom) a free-typed percentage,
// so "Custom" can hold a value that happens to equal a preset without snapping back.
const initRate = (): { selection: string; custom: number | null } => {
  const r = num(props.vatRate);
  if (r === null) return { selection: '19', custom: null };
  const asStr = String(r);
  return PRESETS.includes(asStr)
    ? { selection: asStr, custom: null }
    : { selection: 'custom', custom: r };
};
const _init = initRate();
const rateSelection = ref<string>(_init.selection);
const customRate = ref<number | null>(_init.custom);
const vatAmount = ref<number | null>(null);

const isCustom = computed(() => rateSelection.value === 'custom');
const effectiveRate = computed<number | null>(() =>
  isCustom.value ? customRate.value : num(rateSelection.value)
);

// VAT amount as a formatted EUR string for the helper line under the rate.
const vatDisplay = computed(() => {
  if (vatAmount.value === null) return '';
  const loc = locale.value === 'en' ? 'en-US' : 'de-DE';
  return new Intl.NumberFormat(loc, { style: 'currency', currency: 'EUR' }).format(vatAmount.value);
});

// Derive the two non-typed fields from the typed base + rate. VAT is always rate-driven.
const solve = () => {
  const rate = effectiveRate.value;
  if (rate === null) return;

  if (mode.value === 'net') {
    if (netLocal.value === null) {
      vatAmount.value = null;
      grossLocal.value = null;
    } else {
      vatAmount.value = round2((netLocal.value * rate) / 100);
      grossLocal.value = round2(netLocal.value + vatAmount.value);
    }
    emit('update:gross', grossLocal.value);
  } else {
    if (grossLocal.value === null) {
      vatAmount.value = null;
      netLocal.value = null;
    } else {
      netLocal.value = round2(grossLocal.value / (1 + rate / 100));
      vatAmount.value = round2(grossLocal.value - netLocal.value);
    }
    emit('update:net', netLocal.value);
  }
};

const emitRate = () => {
  const rate = effectiveRate.value;
  emit('update:vatRate', rate === null ? null : String(rate));
};

const onNet = (v: Amount) => {
  netLocal.value = num(v);
  emit('update:net', netLocal.value);
  solve();
};
const onGross = (v: Amount) => {
  grossLocal.value = num(v);
  emit('update:gross', grossLocal.value);
  solve();
};
const onRateSelect = (v: any) => {
  const sel = v == null ? '0' : String(v);
  // Seed the custom field from the current rate so the amount doesn't jump on switch.
  if (sel === 'custom' && customRate.value === null) customRate.value = num(rateSelection.value);
  rateSelection.value = sel;
  emitRate();
  solve();
};
const onCustomRate = (v: Amount) => {
  customRate.value = num(v);
  emitRate();
  solve();
};
const setMode = (m: Mode) => {
  if (m === mode.value) return;
  mode.value = m;
  solve();
};

const segClass = (m: Mode) =>
  [
    'tw-px-3 tw-py-1 tw-text-sm tw-rounded tw-transition-colors',
    mode.value === m
      ? 'tw-bg-neutral-800 tw-text-white'
      : 'tw-text-neutral-600 hover:tw-text-neutral-900',
  ].join(' ');

// Keep local mirrors in sync with external prop changes (async edit-mode load, or the payment
// discount watcher writing back into `amount`). Echoes of our own emits arrive equal to the
// local value, so they short-circuit here and never loop.
watch(
  () => [props.net, props.gross],
  ([n, g]) => {
    const net = num(n);
    const gross = num(g);
    if (net === netLocal.value && gross === grossLocal.value) return;
    netLocal.value = net;
    grossLocal.value = gross;
    if (net !== null && gross !== null) vatAmount.value = round2(gross - net);
  }
);

// Initialise from whatever was passed in WITHOUT clobbering a loaded record:
// - both present  -> show VAT = gross - net and infer the rate (never recompute the amounts)
// - one present   -> compute the missing counterpart from the default/known rate
const net0 = netLocal.value;
const gross0 = grossLocal.value;
if (net0 !== null && gross0 !== null) {
  vatAmount.value = round2(gross0 - net0);
  if (num(props.vatRate) === null) {
    const implied = net0 !== 0 ? round2((gross0 / net0 - 1) * 100) : null;
    if (implied !== null) {
      const asStr = String(implied);
      if (PRESETS.includes(asStr)) {
        rateSelection.value = asStr;
        customRate.value = null;
      } else {
        rateSelection.value = 'custom';
        customRate.value = implied;
      }
    }
  }
} else if (net0 !== null || gross0 !== null) {
  solve();
}
</script>
